/**
 * OAuthCallback — `/login#access_token=...&refresh_token=...&expires_at=...` を消費する
 *
 * App.tsx は変更不可なので、LoginPage 内で hash の存在を判定したときにこの
 * コンポーネントを描画する。
 *
 *  1) hash を `parseOAuthFragment` で解読
 *  2) 成功 → セッション保存 → onSuccess(session)
 *  3) 失敗 → エラー表示 + onBack で戻れるボタン
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { parseOAuthFragment,parseMfaFragment,isMfaChallenge, writeStoredSession, type AuthResponse } from "./api";

export interface OAuthCallbackProps {
  hash: string;
  onSuccess: (session: AuthResponse) => void;
  onBack: () => void;
}

export function OAuthCallback({ hash, onSuccess, onBack }: OAuthCallbackProps): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const session = parseMfaFragment(hash)??parseOAuthFragment(hash);
    if (!session) {
      setError(t("auth.oauth.callback.error"));
      return;
    }
    if(!isMfaChallenge(session))writeStoredSession(session);
    // Clean hash so a refresh doesn't re-parse stale tokens.
    if (typeof window !== "undefined" && window.history.replaceState) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    onSuccess(session);
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div role="alert" aria-live="assertive">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle size={20} className="text-danger" aria-hidden />
          <h2 className="text-lg font-bold text-fg-primary">{t("auth.oauth.callback.title")}</h2>
        </div>
        <p className="text-sm text-danger mb-4">{error}</p>
        <Button variant="secondary" onClick={onBack} className="w-full">
          {t("auth.oauth.callback.back")}
        </Button>
      </div>
    );
  }

  return (
    <div role="status" aria-live="polite" className="text-center py-4">
      <Loader2
        size={28}
        className="text-brand-600 mx-auto mb-3 animate-spin"
        aria-hidden
      />
      <h2 className="text-lg font-bold text-fg-primary mb-1">
        {t("auth.oauth.callback.title")}
      </h2>
      <p className="text-sm text-fg-secondary">{t("auth.oauth.callback.subtitle")}</p>
    </div>
  );
}
